// src/services/s3Service.ts

import {
  S3Client,
  ListObjectsV2Command,
  GetObjectCommand,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import {
  S3_BUCKET_NAME,
  AWS_REGION,
  PHOTOS_PREFIX,
  THUMBNAILS_PREFIX,
  URL_EXPIRY_SECONDS,
} from '../models/constants';
import type { AwsCredentials } from '../models/types';

export interface IS3Service {
  listYearPrefixes(): Promise<string[]>;
  listEventPrefixes(year: string): Promise<string[]>;
  listPhotoKeys(year: string, event: string): Promise<string[]>;
  generatePresignedUrl(key: string, expiresInSeconds?: number): Promise<string>;
  generateThumbnailUrl(key: string, expiresInSeconds?: number): Promise<string>;
  generateDownloadUrl(key: string, filename: string): Promise<string>;
}

export class S3Service implements IS3Service {
  private client: S3Client;

  constructor(credentials: AwsCredentials) {
    this.client = new S3Client({
      region: AWS_REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });
  }

  /**
   * Update the S3Client with new credentials (e.g. after refresh).
   */
  updateCredentials(credentials: AwsCredentials): void {
    this.client = new S3Client({
      region: AWS_REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });
  }

  /**
   * List year prefixes under jaw-all-photos/.
   * Returns clean year strings, e.g. ["2024", "2023", "2022"].
   */
  async listYearPrefixes(): Promise<string[]> {
    const years: string[] = [];
    let continuationToken: string | undefined;

    do {
      const command = new ListObjectsV2Command({
        Bucket: S3_BUCKET_NAME,
        Prefix: `${PHOTOS_PREFIX}/`,
        Delimiter: '/',
        ContinuationToken: continuationToken,
      });

      const response = await this.client.send(command);

      if (response.CommonPrefixes) {
        for (const prefix of response.CommonPrefixes) {
          if (prefix.Prefix) {
            // prefix.Prefix looks like "jaw-all-photos/2024/"
            const year = prefix.Prefix.replace(`${PHOTOS_PREFIX}/`, '').replace(/\/$/, '');
            if (year) {
              years.push(year);
            }
          }
        }
      }

      continuationToken = response.IsTruncated ? response.NextContinuationToken : undefined;
    } while (continuationToken);

    return years;
  }

  /**
   * List event prefixes under jaw-all-photos/{year}/.
   * Returns clean event strings, e.g. ["wedding", "graduation"].
   */
  async listEventPrefixes(year: string): Promise<string[]> {
    const events: string[] = [];
    let continuationToken: string | undefined;

    do {
      const command = new ListObjectsV2Command({
        Bucket: S3_BUCKET_NAME,
        Prefix: `${PHOTOS_PREFIX}/${year}/`,
        Delimiter: '/',
        ContinuationToken: continuationToken,
      });

      const response = await this.client.send(command);

      if (response.CommonPrefixes) {
        for (const prefix of response.CommonPrefixes) {
          if (prefix.Prefix) {
            // prefix.Prefix looks like "jaw-all-photos/2024/wedding/"
            const event = prefix.Prefix.replace(`${PHOTOS_PREFIX}/${year}/`, '').replace(/\/$/, '');
            if (event) {
              events.push(event);
            }
          }
        }
      }

      continuationToken = response.IsTruncated ? response.NextContinuationToken : undefined;
    } while (continuationToken);

    return events;
  }

  /**
   * Supported photo file extensions (lowercase). Videos and other files are excluded.
   */
  private static readonly PHOTO_EXTENSIONS = new Set([
    '.jpg', '.jpeg', '.png', '.heic', '.heif', '.tiff', '.tif', '.gif', '.webp', '.bmp',
  ]);

  /**
   * Checks if a filename has a supported photo extension.
   */
  private isPhotoFile(key: string): boolean {
    const lastDot = key.lastIndexOf('.');
    if (lastDot === -1) return false;
    const ext = key.substring(lastDot).toLowerCase();
    return S3Service.PHOTO_EXTENSIONS.has(ext);
  }

  /**
   * List photo keys under jaw-all-photos/{year}/{event}/.
   * Returns full S3 keys, e.g. ["jaw-all-photos/2024/wedding/IMG_001.jpg"].
   * Filters out video files and other non-photo formats.
   * Handles pagination for folders with > 1000 objects.
   */
  async listPhotoKeys(year: string, event: string): Promise<string[]> {
    const keys: string[] = [];
    let continuationToken: string | undefined;

    do {
      const command = new ListObjectsV2Command({
        Bucket: S3_BUCKET_NAME,
        Prefix: `${PHOTOS_PREFIX}/${year}/${event}/`,
        ContinuationToken: continuationToken,
      });

      const response = await this.client.send(command);

      if (response.Contents) {
        for (const object of response.Contents) {
          if (object.Key) {
            // Skip the "folder" key itself (e.g. "jaw-all-photos/2024/wedding/")
            if (object.Key !== `${PHOTOS_PREFIX}/${year}/${event}/` && this.isPhotoFile(object.Key)) {
              keys.push(object.Key);
            }
          }
        }
      }

      continuationToken = response.IsTruncated ? response.NextContinuationToken : undefined;
    } while (continuationToken);

    return keys;
  }

  /**
   * Generate a pre-signed URL for a full-resolution photo.
   * Default expiry is 60 minutes (3600 seconds).
   */
  async generatePresignedUrl(key: string, expiresInSeconds: number = URL_EXPIRY_SECONDS): Promise<string> {
    const command = new GetObjectCommand({
      Bucket: S3_BUCKET_NAME,
      Key: key,
    });

    return getSignedUrl(this.client, command, { expiresIn: expiresInSeconds });
  }

  /**
   * Generate a pre-signed URL for a thumbnail image.
   * Converts a full-res key to the thumbnail prefix path.
   * Default expiry is 60 minutes (3600 seconds).
   */
  async generateThumbnailUrl(key: string, expiresInSeconds: number = URL_EXPIRY_SECONDS): Promise<string> {
    // Convert full-res key to thumbnail key
    // e.g. "jaw-all-photos/2024/wedding/IMG_001.HEIC" → "jaw-all-thumbnails/2024/wedding/IMG_001.jpg"
    // The Lambda always saves thumbnails as .jpg regardless of original extension
    let thumbnailKey = key.replace(PHOTOS_PREFIX, THUMBNAILS_PREFIX);

    // Replace the original extension with .jpg
    const lastDotIndex = thumbnailKey.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      thumbnailKey = thumbnailKey.substring(0, lastDotIndex) + '.jpg';
    }

    const command = new GetObjectCommand({
      Bucket: S3_BUCKET_NAME,
      Key: thumbnailKey,
    });

    return getSignedUrl(this.client, command, { expiresIn: expiresInSeconds });
  }

  /**
   * Generate a pre-signed URL for downloading a photo with Content-Disposition attachment header.
   * This triggers a file download in the browser with the original filename preserved.
   */
  async generateDownloadUrl(key: string, filename: string): Promise<string> {
    const command = new GetObjectCommand({
      Bucket: S3_BUCKET_NAME,
      Key: key,
      ResponseContentDisposition: `attachment; filename="${filename}"`,
    });

    return getSignedUrl(this.client, command, { expiresIn: URL_EXPIRY_SECONDS });
  }
}
